import React, { useState, useEffect } from 'react';
import { useVCOnboarding } from '../../../contexts/VCOnboardingContext';
import {
  VCInvestmentFocus,
  InvestmentStage,
  InvestmentSector,
  INVESTMENT_STAGE_LABELS,
  INVESTMENT_SECTOR_LABELS,
} from '../../../types/vcOnboarding';
import styles from '../VCOnboarding.module.css';

const Step2InvestmentFocus: React.FC = () => {
  const { data, saveStep, nextStep, previousStep, loading } = useVCOnboarding();
  
  const [formData, setFormData] = useState<VCInvestmentFocus>({
    preferredStages: [],
    sectors: [],
    portfolioCompanies: 0,
  });
  
  const [errors, setErrors] = useState<Partial<Record<keyof VCInvestmentFocus, string>>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (data.step2_investment_focus) {
      setFormData(data.step2_investment_focus);
    }
  }, [data.step2_investment_focus]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof VCInvestmentFocus, string>> = {};
    
    if (formData.preferredStages.length === 0) {
      newErrors.preferredStages = 'Please select at least one investment stage';
    }
    
    if (formData.sectors.length === 0) {
      newErrors.sectors = 'Please select at least one sector';
    }
    
    if (formData.portfolioCompanies < 0) {
      newErrors.portfolioCompanies = 'Please enter a valid number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setIsSaving(true);
      await saveStep(2, formData);
      nextStep();
    } catch (error) {
      console.error('Error saving step 2:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleStage = (stage: InvestmentStage) => {
    setFormData(prev => ({
      ...prev,
      preferredStages: prev.preferredStages.includes(stage)
        ? prev.preferredStages.filter(s => s !== stage)
        : [...prev.preferredStages, stage],
    }));
    if (errors.preferredStages) {
      setErrors(prev => ({ ...prev, preferredStages: undefined }));
    }
  };

  const toggleSector = (sector: InvestmentSector) => {
    setFormData(prev => ({
      ...prev,
      sectors: prev.sectors.includes(sector)
        ? prev.sectors.filter(s => s !== sector)
        : [...prev.sectors, sector],
    }));
    if (errors.sectors) {
      setErrors(prev => ({ ...prev, sectors: undefined }));
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className={styles.card}>
        <h2 className={styles.cardTitle}>Investment focus</h2>
        <p className={styles.cardDescription}>
          What stages and sectors do you invest in?
        </p>

        <div className={styles.formGroup}>
          <label className={styles.label}>
            Preferred Stage
          </label>
          <div className={styles.toggleGroup}>
            {(Object.keys(INVESTMENT_STAGE_LABELS) as InvestmentStage[]).map((stage) => (
              <button
                key={stage}
                type="button"
                className={`${styles.toggleButton} ${
                  formData.preferredStages.includes(stage) ? styles.active : ''
                }`}
                onClick={() => toggleStage(stage)}
                disabled={isSaving || loading}
              >
                {INVESTMENT_STAGE_LABELS[stage]}
              </button>
            ))}
          </div>
          {errors.preferredStages && (
            <div className={styles.errorMessage}>{errors.preferredStages}</div>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>
            Sectors (Select all that apply)
          </label>
          <div className={styles.toggleGroup}>
            {(Object.keys(INVESTMENT_SECTOR_LABELS) as InvestmentSector[]).map((sector) => (
              <button
                key={sector}
                type="button"
                className={`${styles.toggleButton} ${
                  formData.sectors.includes(sector) ? styles.active : ''
                }`}
                onClick={() => toggleSector(sector)}
                disabled={isSaving || loading}
              >
                {INVESTMENT_SECTOR_LABELS[sector]}
              </button>
            ))}
          </div>
          {errors.sectors && (
            <div className={styles.errorMessage}>{errors.sectors}</div>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="portfolioCompanies">
            Number of Portfolio Companies
          </label>
          <input
            id="portfolioCompanies"
            type="number"
            min="0"
            className={styles.input}
            placeholder="e.g., 25"
            value={formData.portfolioCompanies || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              portfolioCompanies: parseInt(e.target.value) || 0,
            }))}
            disabled={isSaving || loading}
          />
          {errors.portfolioCompanies && (
            <div className={styles.errorMessage}>{errors.portfolioCompanies}</div>
          )}
        </div>

        <div className={styles.buttonGroup}>
          <button
            type="button"
            className={`${styles.button} ${styles.buttonSecondary}`}
            onClick={previousStep}
            disabled={isSaving || loading}
          >
            <svg className={styles.buttonIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <button
            type="submit"
            className={`${styles.button} ${styles.buttonPrimary}`}
            disabled={isSaving || loading}
          >
            {isSaving ? (
              <>
                <span className={styles.loadingSpinner} />
                Saving...
              </>
            ) : (
              <>
                Continue
                <svg className={styles.buttonIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default Step2InvestmentFocus;

